import { isNil, omitBy } from 'lodash';
import { useEffect, useRef } from 'react';
import { useGetClientApplication } from '../../api';
import { useLocation } from 'react-router-dom';
import TagManager from 'react-gtm-module';
import useClient from 'hooks/useClient';
import useDataLayerHashedValues from './useDataLayerHashedValues';

const tagManagerArgs = {
  gtmId: process.env.REACT_APP_GTM || '',
};

export default function useDataLayer() {
  const { pathname } = useLocation();
  const { client } = useClient();
  const { data: applicationData } = useGetClientApplication();
  const prevLocation = useRef<string | null>();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const {
    getHashedEmail,
    getHashedFirstName,
    getHashedLastName,
    getHashedTelephoneNumber,
  } = useDataLayerHashedValues();

  const logPageChange = async () => {
    try {
      const dataLayer: Record<string, any> = {
        page: {
          url: window.location.pathname,
          referrer: prevLocation.current ?? document.referrer,
        },
      };

      if (client) {
        dataLayer.client = omitBy(
          {
            id: client.id,
            dateOfBirth: client.dateOfBirth,
            number: client.number,
            status: client.status,
            age: client.age,
            identifiedBy: client.identifiedBy,
            registeredBy: client.registeredBy,
            htn: await getHashedTelephoneNumber(
              client.mobilePhone?.mobilePhone,
            ),
            hma: await getHashedEmail(client?.email?.email),
            address: omitBy(
              {
                hfn: await getHashedFirstName(client.firstName),
                hln: await getHashedLastName(client.lastName),
                city: client.declaredAddress?.location1,
                'postal-code': client.declaredAddress?.postalCode,
              },
              isNil,
            ),
          },
          isNil,
        );
      }

      if (applicationData) {
        if (applicationData.status === 'OPEN') {
          dataLayer.application = omitBy(
            {
              id: applicationData.id,
              amount: applicationData.amount,
              term: applicationData.term,
              type: applicationData.type,
            },
            isNil,
          );
        } else {
          dataLayer.application = omitBy(
            {
              status: applicationData.status,
              resolution: applicationData.resolution,
              resolutionDetail: applicationData.resolutionDetail,
              resolutionMessage: applicationData.resolutionMessage,
            },
            isNil,
          );
        }
      }

      TagManager.dataLayer({
        dataLayer: {
          event: 'pageChange',
          ...dataLayer,
        },
      });

      TagManager.dataLayer({
        dataLayer: {
          event: 'page_view',
          ...dataLayer,
        },
      });

      prevLocation.current = window.location.pathname;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error logging page change do GTM dataLayer', e);
    }
  };

  const decouncedLogPageChange = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(logPageChange, 2000);
  };

  useEffect(() => {
    if (!tagManagerArgs?.gtmId.length) return;

    TagManager.initialize(tagManagerArgs);

    // Track basic JavaScript errors
    window.addEventListener('error', function (e) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'error',
          message: e.message,
          stacktrace: e.error?.stack || 'error stack undefined',
          path: window.location.pathname,
        },
      });
    });

    if (
      process.env.REACT_APP_ENV === 'production' ||
      process.env.REACT_APP_ENV === 'staging' ||
      process.env.REACT_APP_ENV === 'test'
    ) {
      // Log the initial page load
      logPageChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    decouncedLogPageChange();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, applicationData]);
}
